import { Phone, Mail, MapPin, Map as MapIcon, ChevronRight, Youtube, Instagram, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { ADDRESS, PHONE_NUMBER, EMAIL, GOOGLE_MAPS_LINK, YOUTUBE_LINK, INSTAGRAM_LINK, FACEBOOK_LINK } from '../constants';

const Contact: React.FC = () => {
  return (
    <>
      <SEO
        title="Contact Us"
        description="Get in touch with Palmal Decorators for your event needs. Call us or visit our office in Midnapore."
        url="/contact"
      />

      {/* Hero Section */}
      <div className="bg-maroon-950 pt-24 pb-16 text-center text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/black-linen.png')] opacity-30"></div>

        <nav className="relative z-10 flex justify-center items-center text-sm mb-4 animate-fade-in" aria-label="Breadcrumb">
          <Link to="/" className="text-gold-400 hover:text-white transition-colors uppercase tracking-widest font-bold text-xs md:text-sm">Home</Link>
          <ChevronRight className="w-4 h-4 mx-2 text-gray-500" />
          <span className="text-white uppercase tracking-widest font-bold text-xs md:text-sm">Contact</span>
        </nav>

        <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4 relative z-10 animate-fade-in-up">Get in Touch</h1>
        <p className="text-gold-400 uppercase tracking-widest text-sm relative z-10 animate-fade-in-up">Let's Plan Your Dream Event</p>
      </div>

      <div className="bg-cream-50 min-h-screen py-12 md:py-20 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            <div className="space-y-8">
              <div className="bg-white p-6 md:p-10 rounded-2xl shadow-2xl border border-gold-100 relative overflow-hidden group">
                <h3 className="text-2xl md:text-3xl font-serif font-bold mb-6 md:mb-10 text-maroon-900 relative z-10">Contact Information</h3>

                <div className="space-y-8 relative z-10">
                  <div className="flex items-center group/item p-3 -mx-3 rounded-xl hover:bg-white/60 transition-all duration-300">
                    <div className="w-14 h-14 bg-gold-100 rounded-full flex items-center justify-center mr-6 shadow-inner shrink-0">
                      <Phone className="w-6 h-6 text-maroon-900" />
                    </div>
                    <div>
                      <h4 className="font-bold text-maroon-900 text-lg md:text-xl font-serif mb-1">Phone</h4>
                      <p className="text-gray-600 text-base md:text-lg tracking-wide">{PHONE_NUMBER}</p>
                    </div>
                  </div>

                  <div className="flex items-center group/item p-3 -mx-3 rounded-xl hover:bg-white/60 transition-all duration-300">
                    <div className="w-14 h-14 bg-gold-100 rounded-full flex items-center justify-center mr-6 shadow-inner shrink-0">
                      <Mail className="w-6 h-6 text-maroon-900" />
                    </div>
                    <div>
                      <h4 className="font-bold text-maroon-900 text-lg md:text-xl font-serif mb-1">Email</h4>
                      <p className="text-gray-600 text-base md:text-lg tracking-wide break-all">{EMAIL}</p>
                    </div>
                  </div>

                  <div className="flex items-center group/item p-3 -mx-3 rounded-xl hover:bg-white/60 transition-all duration-300">
                    <div className="w-14 h-14 bg-gold-100 rounded-full flex items-center justify-center mr-6 shadow-inner shrink-0">
                      <MapPin className="w-6 h-6 text-maroon-900" />
                    </div>
                    <div>
                      <h4 className="font-bold text-maroon-900 text-lg md:text-xl font-serif mb-1">Office Address</h4>
                      <p className="text-gray-600 mb-3 text-sm md:text-base">{ADDRESS}</p>
                      <a
                        href={GOOGLE_MAPS_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs font-bold text-white bg-maroon-900 px-4 py-2 rounded-full hover:bg-gold-500 hover:shadow-lg transition-all transform hover:-translate-y-0.5 uppercase tracking-widest shadow-md"
                      >
                        <MapIcon className="w-3 h-3 mr-2" /> Get Directions
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <a href={YOUTUBE_LINK} target="_blank" rel="noopener noreferrer" className="bg-white p-4 md:p-6 rounded-2xl shadow-xl border border-gold-100 flex items-center justify-between group hover:bg-gold-50 transition-colors mb-6 md:mb-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                    <Youtube className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-maroon-900 text-lg">Watch on YouTube</h3>
                    <p className="text-gray-600 text-sm">Latest event videos</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gold-600 group-hover:translate-x-1 transition-all" />
              </a>

              <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" className="bg-white p-4 md:p-6 rounded-2xl shadow-xl border border-gold-100 flex items-center justify-between group hover:bg-gold-50 transition-colors mb-6 md:mb-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                    <Instagram className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-maroon-900 text-lg">Follow on Instagram</h3>
                    <p className="text-gray-600 text-sm">Photos & Updates</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gold-600 group-hover:translate-x-1 transition-all" />
              </a>

              <a href={FACEBOOK_LINK} target="_blank" rel="noopener noreferrer" className="bg-white p-4 md:p-6 rounded-2xl shadow-xl border border-gold-100 flex items-center justify-between group hover:bg-gold-50 transition-colors mb-6 md:mb-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                    <Facebook className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-maroon-900 text-lg">Like on Facebook</h3>
                    <p className="text-gray-600 text-sm">Community & Reviews</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gold-600 group-hover:translate-x-1 transition-all" />
              </a>

              <div className="bg-white p-2 rounded-2xl shadow-xl h-64 md:h-80 overflow-hidden relative border-4 border-white">
                <iframe
                  title="Google Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3688.473604845529!2d87.31032877529577!3d22.411193379607077!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a1d5bb5ea192f69%3A0x3ae75397d4126135!2sPalmal%20Decorators!5e0!3m2!1sen!2sin!4v1768804811539!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  className="rounded-xl w-full h-full"
                ></iframe>
              </div>
            </div>

            <div className="sticky top-28">
              <ContactForm />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;